import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Star, Copy, Plus, X, Settings2, RefreshCw, Link as LinkIcon, BarChart3, TrendingDown,
         Smile, Frown, Meh, ThumbsUp, ThumbsDown } from "lucide-react";

/** Iter 253u · Mood chip rendered next to a client comment.
 * `mood` is one of: delight | positive | neutral | concern | disappointment. */
function MoodChip({ mood, one_liner, testid }) {
  const cfg = {
    delight:        { cls: "bg-emerald-100 text-emerald-800 border-emerald-300", icon: ThumbsUp, label: "Delight" },
    positive:       { cls: "bg-emerald-50 text-emerald-700 border-emerald-200",   icon: Smile,    label: "Positive" },
    neutral:        { cls: "bg-slate-100 text-slate-700 border-slate-300",        icon: Meh,      label: "Neutral" },
    concern:        { cls: "bg-amber-50 text-amber-800 border-amber-300",         icon: Frown,    label: "Concern" },
    disappointment: { cls: "bg-rose-100 text-rose-800 border-rose-300",           icon: ThumbsDown, label: "Disappointment" },
  }[mood] || { cls: "bg-slate-50 text-slate-500 border-slate-200", icon: Meh, label: mood };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 border rounded uppercase tracking-wider ${cfg.cls}`}
          title={one_liner || cfg.label} data-testid={testid}>
      <Icon className="w-2.5 h-2.5" /> {cfg.label}
    </span>
  );
}

export default function FeedbackRequests() {
  const { activeWorkspaceId } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [detail, setDetail] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const load = useCallback(async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/feedback/requests`);
      setRows(data || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load requests")); }
    finally { setLoading(false); }
  }, [activeWorkspaceId]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="px-4 sm:px-6 md:px-10 py-6">
      <PageHeader
        eyebrow="Feedback"
        title="Client feedback"
        subtitle="Send a magic-link to your client and gather rating + comments per team member."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-none" onClick={() => (window.location.href = "/app/feedback/nps")} data-testid="feedback-nps-btn">
              <BarChart3 className="w-3.5 h-3.5 mr-1" /> NPS report
            </Button>
            <Button variant="outline" className="rounded-none" onClick={() => (window.location.href = "/app/feedback/sentiment-trends")} data-testid="feedback-trends-btn">
              <TrendingDown className="w-3.5 h-3.5 mr-1" /> Sentiment trends
            </Button>
            <Button variant="outline" className="rounded-none" onClick={() => setSettingsOpen(true)} data-testid="feedback-settings-btn">
              <Settings2 className="w-3.5 h-3.5 mr-1" /> Settings
            </Button>
            <Button variant="outline" className="rounded-none" onClick={load} disabled={loading} data-testid="feedback-refresh-btn">
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Button className="rounded-none" onClick={() => setCreating(true)} data-testid="feedback-new-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> New request
            </Button>
          </div>
        }
      />
      <div className="mt-6">
        {rows.length === 0 && !loading && (
          <div className="border border-dashed border-border p-10 text-center text-sm text-muted-foreground" data-testid="feedback-empty">
            No feedback requests yet. Click "New request" to send one to a client.
          </div>
        )}
        <div className="grid gap-3">
          {rows.map((r) => (
            <div key={r.id} className="border border-border p-3 flex items-start justify-between gap-4 hover:border-primary/50 cursor-pointer" onClick={() => setDetail(r)} data-testid={`feedback-row-${r.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold">{r.title}</span>
                  <Badge variant="outline" className="rounded-none text-[9px]">{r.status}</Badge>
                  {r.submission_count > 0 && (
                    <Badge variant="outline" className="rounded-none text-[9px] border-emerald-500 text-emerald-700">
                      {r.submission_count} response{r.submission_count === 1 ? "" : "s"}
                    </Badge>
                  )}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5 truncate">{r.description}</div>
                <div className="text-[10px] text-muted-foreground mt-1">
                  Client: {r.client_name || r.client_email} · {r.target_user_ids?.length || 0} team member(s) rated · created {new Date(r.created_at).toLocaleDateString()}
                </div>
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  const url = `${window.location.origin}/feedback/${r.magic_token}`;
                  navigator.clipboard.writeText(url).then(() => toast.success("Public link copied"));
                }}
                className="border border-border px-2 py-1 text-[10px] inline-flex items-center gap-1 hover:border-primary"
                data-testid={`feedback-copy-link-${r.id}`}
                title="Copy public feedback link"
              >
                <LinkIcon className="w-3 h-3" /> Copy link
              </button>
            </div>
          ))}
        </div>
      </div>
      {creating && (
        <NewFeedbackDialog wsId={activeWorkspaceId} onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />
      )}
      {detail && (
        <FeedbackDetailDialog wsId={activeWorkspaceId} request={detail} onClose={() => setDetail(null)} />
      )}
      {settingsOpen && (
        <SettingsDialog wsId={activeWorkspaceId} onClose={() => setSettingsOpen(false)} />
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────

function NewFeedbackDialog({ wsId, onClose, onCreated }) {
  const [form, setForm] = useState({
    title: "", description: "",
    entity_kind: "brand",   // brand | project
    brand_id: "", project_id: "",
    client_email: "", client_name: "",
    portal_login_required: false,
  });
  const [brands, setBrands] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectMembers, setProjectMembers] = useState([]);
  const [wsMembers, setWsMembers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [customParams, setCustomParams] = useState([]);
  const [standardParams, setStandardParams] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancel = false;
    Promise.all([
      api.get(`/workspaces/${wsId}/brands`).catch(() => ({ data: [] })),
      api.get(`/workspaces/${wsId}/projects`).catch(() => ({ data: [] })),
      api.get(`/workspaces/${wsId}/members`).catch(() => ({ data: [] })),
      api.get(`/workspaces/${wsId}/feedback/parameters/standard`).catch(() => ({ data: { parameters: [] } })),
    ]).then(([b, p, m, sp]) => {
      if (cancel) return;
      setBrands(Array.isArray(b.data) ? b.data : (b.data?.items || []));
      setProjects(Array.isArray(p.data) ? p.data : []);
      setWsMembers(Array.isArray(m.data) ? m.data : []);
      setStandardParams(sp.data?.parameters || []);
    });
    return () => { cancel = true; };
  }, [wsId]);

  // When project changes, load its members
  useEffect(() => {
    if (form.entity_kind !== "project" || !form.project_id) { setProjectMembers([]); return; }
    api.get(`/projects/${form.project_id}/members`).then((r) => setProjectMembers(r.data || [])).catch(() => setProjectMembers([]));
  }, [form.entity_kind, form.project_id]);

  const memberSource = form.entity_kind === "project" && form.project_id
    ? projectMembers.map((pm) => ({ user_id: pm.user_id, name: pm.user?.name || pm.name, email: pm.user?.email || pm.email }))
    : wsMembers.map((m) => ({ user_id: m.user_id, name: m.name, email: m.email }));

  const toggleUser = (uid) => {
    setSelectedUsers((s) => s.includes(uid) ? s.filter((x) => x !== uid) : [...s, uid]);
  };

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    if (!form.client_email.trim()) { toast.error("Client email required"); return; }
    if (form.entity_kind === "brand" && !form.brand_id) { toast.error("Pick a brand"); return; }
    if (form.entity_kind === "project" && !form.project_id) { toast.error("Pick a project"); return; }
    if (selectedUsers.length === 0) { toast.error("Select at least one team member"); return; }
    setBusy(true);
    try {
      const parameters = [
        ...standardParams.map((p) => ({ name: p, type: "standard" })),
        ...customParams.filter((c) => c.trim()).map((c) => ({ name: c.trim(), type: "custom" })),
      ];
      const payload = {
        title: form.title, description: form.description || null,
        brand_id: form.entity_kind === "brand" ? form.brand_id : null,
        project_id: form.entity_kind === "project" ? form.project_id : null,
        target_user_ids: selectedUsers,
        parameters,
        client_email: form.client_email,
        client_name: form.client_name || null,
        portal_login_required: form.portal_login_required,
      };
      const { data } = await api.post(`/workspaces/${wsId}/feedback/requests`, payload);
      const url = `${window.location.origin}/feedback/${data.request.magic_token}`;
      await navigator.clipboard.writeText(url).catch(() => {});
      toast.success("Feedback request created. Public link copied to clipboard.");
      onCreated();
    } catch (e) { toast.error(apiErrorMessage(e, "Create failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto rounded-none" data-testid="feedback-new-dialog">
        <DialogHeader>
          <DialogTitle>New feedback request</DialogTitle>
          <DialogDescription>Sends a magic-link to the client so they can rate + comment per team member.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="label-eyebrow mb-1 inline-block">Title</Label>
            <Input value={form.title} onChange={(e) => setForm((s) => ({ ...s, title: e.target.value }))}
                   placeholder="e.g. Q1 Campaign Review" className="rounded-none" data-testid="feedback-title" />
          </div>
          <div>
            <Label className="label-eyebrow mb-1 inline-block">Description (visible to client)</Label>
            <Textarea rows={2} value={form.description} onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))}
                      placeholder="A short brief so the client knows what they're rating"
                      className="rounded-none" data-testid="feedback-description" />
          </div>

          <div className="border border-border p-3">
            <div className="flex gap-3 mb-2">
              <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                <input type="radio" checked={form.entity_kind === "brand"} onChange={() => setForm((s) => ({ ...s, entity_kind: "brand", project_id: "" }))}
                       data-testid="feedback-kind-brand" />
                Brand
              </label>
              <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                <input type="radio" checked={form.entity_kind === "project"} onChange={() => setForm((s) => ({ ...s, entity_kind: "project", brand_id: "" }))}
                       data-testid="feedback-kind-project" />
                Project
              </label>
            </div>
            {form.entity_kind === "brand" ? (
              <Select value={form.brand_id} onValueChange={(v) => setForm((s) => ({ ...s, brand_id: v }))}>
                <SelectTrigger className="h-9 rounded-none" data-testid="feedback-brand"><SelectValue placeholder="Pick a brand" /></SelectTrigger>
                <SelectContent>
                  {brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <Select value={form.project_id} onValueChange={(v) => setForm((s) => ({ ...s, project_id: v }))}>
                <SelectTrigger className="h-9 rounded-none" data-testid="feedback-project"><SelectValue placeholder="Pick a project" /></SelectTrigger>
                <SelectContent>
                  {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="border border-border p-3">
            <Label className="label-eyebrow mb-2 inline-block">Team members being rated ({selectedUsers.length})</Label>
            {memberSource.length === 0 && (
              <div className="text-xs text-muted-foreground italic">Pick a project first, or no workspace members found.</div>
            )}
            <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
              {memberSource.map((m) => {
                const on = selectedUsers.includes(m.user_id);
                return (
                  <button
                    key={m.user_id}
                    type="button"
                    onClick={() => toggleUser(m.user_id)}
                    className={`text-[11px] px-2 py-1 border ${on ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border hover:border-primary"}`}
                    data-testid={`feedback-member-${m.user_id}`}
                  >
                    {m.name || m.email}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Client email *</Label>
              <Input type="email" value={form.client_email}
                     onChange={(e) => setForm((s) => ({ ...s, client_email: e.target.value }))}
                     className="rounded-none" data-testid="feedback-client-email" />
            </div>
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Client name</Label>
              <Input value={form.client_name}
                     onChange={(e) => setForm((s) => ({ ...s, client_name: e.target.value }))}
                     className="rounded-none" data-testid="feedback-client-name" />
            </div>
          </div>

          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={form.portal_login_required}
                   onChange={(e) => setForm((s) => ({ ...s, portal_login_required: e.target.checked }))}
                   data-testid="feedback-portal-required" />
            Require client portal login (otherwise magic-link only)
          </label>

          <div className="border border-border p-3">
            <div className="label-eyebrow mb-2">Rating parameters</div>
            <div className="text-[11px] text-muted-foreground mb-2">
              Standard: {standardParams.join(" · ")}
            </div>
            <div className="space-y-1">
              {customParams.map((c, i) => (
                <div key={i} className="flex gap-2 items-center" data-testid={`feedback-custom-param-${i}`}>
                  <Input value={c} onChange={(e) => {
                    const n = [...customParams]; n[i] = e.target.value; setCustomParams(n);
                  }} placeholder="Custom parameter name" className="h-7 text-xs rounded-none" />
                  <button type="button" className="text-muted-foreground hover:text-rose-600"
                          onClick={() => setCustomParams(customParams.filter((_, ix) => ix !== i))}>
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              <Button variant="outline" size="sm" className="rounded-none h-7 text-[10px]"
                      onClick={() => setCustomParams([...customParams, ""])}
                      data-testid="feedback-add-custom-param">
                <Plus className="w-3 h-3 mr-1" /> Add custom parameter
              </Button>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose} data-testid="feedback-new-cancel">Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy} data-testid="feedback-new-submit">
            {busy ? "Creating…" : "Create + copy link"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function FeedbackDetailDialog({ wsId, request, onClose }) {
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    api.get(`/workspaces/${wsId}/feedback/requests/${request.id}`).then((r) => setDetail(r.data)).catch(() => {});
  }, [wsId, request.id]);

  const url = `${window.location.origin}/feedback/${request.magic_token}`;

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto rounded-none" data-testid="feedback-detail-dialog">
        <DialogHeader>
          <DialogTitle>{request.title}</DialogTitle>
          <DialogDescription>
            {request.submission_count || 0} response(s) received · Client: {request.client_name || request.client_email}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="border border-border p-2 text-xs font-mono flex items-center gap-2 bg-muted/30" data-testid="feedback-detail-link">
            {url}
            <button type="button" className="ml-auto border border-border px-2 py-0.5 text-[10px] inline-flex items-center gap-1 hover:border-primary"
                    onClick={() => { navigator.clipboard.writeText(url); toast.success("Copied"); }}>
              <Copy className="w-3 h-3" /> Copy
            </button>
          </div>
          {detail?.submissions?.length === 0 && (
            <div className="text-sm text-muted-foreground italic">Awaiting client response.</div>
          )}
          {detail?.submissions?.map((s) => (
            <div key={s.id} className="border border-border p-3 space-y-2" data-testid={`feedback-submission-${s.id}`}>
              <div className="text-xs flex items-center gap-2 flex-wrap">
                <strong>{s.submitted_by_name || "Client"}</strong>
                <span className="text-muted-foreground">· {new Date(s.submitted_at).toLocaleString()}</span>
                {s.sentiment?.overall && <MoodChip mood={s.sentiment.overall.mood} one_liner={s.sentiment.overall.one_liner}
                                                     testid={`mood-overall-${s.id}`} />}
              </div>
              {s.overall_comment && <div className="text-sm italic border-l-2 border-primary/30 pl-2">"{s.overall_comment}"</div>}
              {(s.per_user || []).map((pu) => (
                <div key={pu.user_id} className="pl-2 border-l border-border">
                  <div className="text-[11px] uppercase text-muted-foreground flex items-center gap-2">
                    <span>{pu.user_id.slice(0, 8)}</span>
                    {s.sentiment?.per_user?.[pu.user_id] && <MoodChip mood={s.sentiment.per_user[pu.user_id].mood}
                                                                            one_liner={s.sentiment.per_user[pu.user_id].one_liner}
                                                                            testid={`mood-${s.id}-${pu.user_id}`} />}
                  </div>
                  <div className="grid grid-cols-2 gap-1 text-xs mt-1">
                    {Object.entries(pu.ratings || {}).map(([k, v]) => (
                      <div key={k} className="flex items-center gap-1">
                        <span className="truncate">{k}</span>
                        <span className="inline-flex ml-auto">
                          {[1, 2, 3, 4, 5].map((n) => (
                            <Star key={n} className={`w-3 h-3 ${n <= v ? "fill-amber-400 text-amber-400" : "text-muted-foreground"}`} />
                          ))}
                        </span>
                      </div>
                    ))}
                  </div>
                  {pu.comment && <div className="text-xs mt-1 text-muted-foreground italic">{pu.comment}</div>}
                </div>
              ))}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function SettingsDialog({ wsId, onClose }) {
  const [autoTask, setAutoTask] = useState(false);
  const [threshold, setThreshold] = useState(2);
  const [autoSendMilestone, setAutoSendMilestone] = useState(false);
  const [runningNow, setRunningNow] = useState(false);
  useEffect(() => {
    api.get(`/workspaces/${wsId}/feedback/settings`).then((r) => {
      setAutoTask(!!r.data?.auto_task_on_low_score);
      setThreshold(r.data?.low_score_threshold || 2);
      setAutoSendMilestone(!!r.data?.auto_send_on_milestone);
    }).catch(() => {});
  }, [wsId]);
  const save = async () => {
    try {
      await api.put(`/workspaces/${wsId}/feedback/settings`, {
        auto_task_on_low_score: autoTask, low_score_threshold: Number(threshold),
        auto_send_on_milestone: autoSendMilestone,
      });
      toast.success("Settings saved");
      onClose();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
  };
  const preview = async () => {
    setRunningNow(true);
    try {
      const { data } = await api.post(
        `/workspaces/${wsId}/feedback/auto-send/run-now`, null,
        { params: { dry_run: true, lookback_days: 7 } });
      const parts = [
        `${data.considered} milestone(s) considered`,
        `${data.sent || 0} would send`,
        `${data.skipped || 0} skipped`,
      ];
      toast.success(parts.join(" · "));
    } catch (e) { toast.error(apiErrorMessage(e, "Preview failed")); }
    finally { setRunningNow(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md rounded-none" data-testid="feedback-settings-dialog">
        <DialogHeader>
          <DialogTitle>Feedback settings</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={autoTask} onChange={(e) => setAutoTask(e.target.checked)}
                   className="mt-1" data-testid="feedback-settings-auto-task" />
            <div>
              <div className="font-medium">Auto-create follow-up task on low score</div>
              <div className="text-xs text-muted-foreground">When a client rates any parameter at or below the threshold, create a "feedback-followup" task for the rated user.</div>
            </div>
          </label>
          <div>
            <Label className="label-eyebrow mb-1 inline-block">Low-score threshold (stars)</Label>
            <Input type="number" min={1} max={5} value={threshold}
                   onChange={(e) => setThreshold(e.target.value)}
                   className="w-24 rounded-none" data-testid="feedback-settings-threshold" />
          </div>
          <div className="border-t border-border pt-3">
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={autoSendMilestone}
                     onChange={(e) => setAutoSendMilestone(e.target.checked)}
                     className="mt-1" data-testid="feedback-settings-auto-send-milestone" />
              <div>
                <div className="font-medium">Auto-send rating link on completed milestones</div>
                <div className="text-xs text-muted-foreground">
                  Every Monday, email a feedback link to the client for any milestone marked <em>done</em> in the last 7 days. Each milestone is emailed at most once.
                </div>
              </div>
            </label>
            <Button variant="outline" size="sm" className="rounded-none mt-2" onClick={preview}
                    disabled={runningNow} data-testid="feedback-settings-preview-run">
              {runningNow ? "Checking…" : "Preview last 7 days"}
            </Button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={save} data-testid="feedback-settings-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
